import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Avatar, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Link, Typography, } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams, } from "react-router-dom";
import { useAddExistingTeamToLeague, useRetrieveAddableTeams, useRetrieveLeague, } from "../api/LeagueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { safeReturnTo } from "../utils/safeReturnTo";
import { useNumericParams } from "../utils/useNumericParams";
export const AddExistingTeamsToLeague = () => {
    const { id: leagueId } = useNumericParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get("returnTo");
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const { data: userLeaguePermissions, isLoading: userLeaguePermissionsIsLoading, error: userLeaguePermissionsError, } = useRetrieveUserLeaguePermissions({ leagueId });
    const { data: league, isLoading: leagueIsLoading, error: leagueError, } = useRetrieveLeague({ id: leagueId });
    const { data: addableTeamsData = [], isLoading: addableTeamsIsLoading, error: addableTeamsError, } = useRetrieveAddableTeams({
        leagueId,
        enabled: userLeaguePermissions?.canModify,
    });
    // Sort teams by name
    const addableTeams = [...addableTeamsData].sort((a, b) => a.name.localeCompare(b.name));
    const addTeamsMutation = useAddExistingTeamToLeague({
        leagueId,
        onMutate: () => {
            // Optional: Show loading state
        },
        onSuccess: () => {
            navigate(safeReturnTo(returnTo, `/leagues/${leagueId}/`));
        },
        onError: () => { },
    });
    const { canModify = false } = userLeaguePermissions || {};
    if (userLeaguePermissionsIsLoading ||
        leagueIsLoading ||
        addableTeamsIsLoading) {
        return _jsx(Loading, {});
    }
    if (userLeaguePermissionsError) {
        return _jsx(ErrorView, { error: userLeaguePermissionsError });
    }
    if (leagueError) {
        return _jsx(ErrorView, { error: leagueError });
    }
    if (addableTeamsError) {
        return _jsx(ErrorView, { error: addableTeamsError });
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    const handleTeamToggle = (teamId) => {
        setSelectedTeamIds((prev) => prev.includes(teamId)
            ? prev.filter((id) => id !== teamId)
            : [...prev, teamId]);
    };
    const handleSelectAll = () => {
        if (selectedTeamIds.length === addableTeams.length) {
            setSelectedTeamIds([]);
        }
        else {
            setSelectedTeamIds(addableTeams.map((team) => team.id));
        }
    };
    const handleSubmit = () => {
        if (selectedTeamIds.length > 0) {
            addTeamsMutation.mutate({ teamIds: selectedTeamIds });
        }
    };
    return (_jsxs(Box, { sx: { maxWidth: 800, mx: "auto", p: 3, mb: 6 }, children: [_jsxs(Typography, { variant: "h4", sx: { textAlign: "center", mb: 1, fontWeight: 600 }, children: ["Add Teams to", " ", _jsx(Link, { component: RouterLink, to: `/leagues/${leagueId}/`, underline: "hover", children: league.name })] }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { textAlign: "center", mb: 4 }, children: "Select the teams you want to add to this league" }), addableTeams.length === 0 ? (_jsxs(Card, { sx: { p: 4, textAlign: "center" }, children: [_jsx(Typography, { variant: "h6", sx: { mb: 2, color: "text.secondary" }, children: "No Teams Available" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: "You don't have any teams available to add to this league. All your teams are either already in this league or you don't have any teams yet." }), _jsx(Button, { component: RouterLink, to: `/leagues/${leagueId}/teams/create/`, variant: "contained", sx: { mr: 2 }, children: "Create New Team" }), _jsx(Button, { component: RouterLink, to: `/leagues/${leagueId}/`, variant: "outlined", children: "Back to League" })] })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                            mb: 3,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }, children: [_jsxs(Typography, { variant: "h6", children: [selectedTeamIds.length, " of ", addableTeams.length, " teams selected"] }), _jsx(Button, { onClick: handleSelectAll, variant: "outlined", size: "small", children: selectedTeamIds.length === addableTeams.length
                                    ? "Deselect All"
                                    : "Select All" })] }), _jsx(Box, { sx: { mb: 4 }, children: addableTeams.map((team) => (_jsx(Card, { sx: {
                                mb: 2,
                                border: selectedTeamIds.includes(team.id)
                                    ? "2px solid"
                                    : "1px solid",
                                borderColor: selectedTeamIds.includes(team.id)
                                    ? "primary.main"
                                    : "grey.300",
                                bgcolor: selectedTeamIds.includes(team.id)
                                    ? "primary.50"
                                    : "background.paper",
                                cursor: "pointer",
                            }, onClick: () => handleTeamToggle(team.id), children: _jsxs(CardContent, { sx: { display: "flex", alignItems: "center", gap: 2, py: 2 }, children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: selectedTeamIds.includes(team.id), onChange: () => handleTeamToggle(team.id), onClick: (e) => e.stopPropagation() }), label: "", sx: { m: 0 }, onClick: (e) => e.stopPropagation() }), _jsx(Avatar, { alt: team.name, src: team.logo || undefined, sx: { width: 56, height: 56 }, children: team.name.match(/\b(\w)/g)?.join("") }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { variant: "h6", children: team.name }), team.nickname && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: team.nickname })), team.establishedYear && (_jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["EST \u2022 ", team.establishedYear] }))] })] }) }, team.id))) }), _jsxs(Box, { sx: { display: "flex", justifyContent: "center", gap: 2 }, children: [_jsx(Button, { onClick: handleSubmit, disabled: selectedTeamIds.length === 0 || addTeamsMutation.isLoading, variant: "contained", size: "large", sx: { px: 4 }, children: addTeamsMutation.isLoading
                                    ? "Adding Teams..."
                                    : `Add ${selectedTeamIds.length} Team${selectedTeamIds.length !== 1 ? "s" : ""}` }), _jsx(Button, { component: RouterLink, to: `/leagues/${leagueId}/add-team/`, variant: "outlined", size: "large", disabled: addTeamsMutation.isLoading, children: "Back" })] })] }))] }));
};
